import { useForm } from 'react-hook-form';

import { useTrackFormState } from '@/hooks/useTrackFormState';
import { useVoiceSTT, useVoiceTTS } from '@/hooks/useVoiceProviders';
import {
  adoptLegacyDefault,
  fromForm,
  SpeechStrategyForm,
  speechStrategyResolver,
  toForm,
} from '@/modules/aiAgents/components/SpeechStrategy/helpers';
import { useSpeechStrategy } from '@/modules/aiAgents/hooks/useSpeechStrategy';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';
import { selectIsFormDirty } from '@/redux/forms/selectors';
import { SpeechStrategy } from '@/shared/types/speechStrategy';
import { RootState } from '@/shared/types/state';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';

export const SPEECH_STRATEGY_FORM_ID = 'speech-strategy';

export const selectIsSpeechStrategyDirty = (state: RootState) =>
  selectIsFormDirty(state, SPEECH_STRATEGY_FORM_ID);

interface Params {
  brainId: string;
  language: string;
  isPrimary: boolean;
}

/**
 * The agent's speech strategy as a form that edits the row of one language. The form holds every
 * stored row, so a save replaces the map without losing the languages it does not show.
 */
export const useSpeechStrategyForm = ({ brainId, language, isPrimary }: Params) => {
  const { strategy, getStatus, refetch, save, saveStatus, canRead, canWrite } =
    useSpeechStrategy(brainId);
  // The step sources come from the speech models catalog and the hosted voice catalog
  const { listStatus, providersStatus, refetchList, refetchProviders } = useSpeechModels();
  const { status: sttStatus, refetch: refetchStt } = useVoiceSTT();
  const { status: ttsStatus, refetch: refetchTts } = useVoiceTTS();

  const forRow = (stored: SpeechStrategy) =>
    isPrimary ? adoptLegacyDefault(stored, language) : stored;

  const form = useForm<SpeechStrategyForm>({
    mode: 'onTouched',
    resolver: speechStrategyResolver(),
    defaultValues: { stt: [], tts: [] },
    values: strategy ? toForm(forRow(strategy), language) : undefined,
    resetOptions: KEEP_DIRTY_VALUES,
  });
  const { isDirty } = form.formState;

  useTrackFormState({ isDirty, formId: SPEECH_STRATEGY_FORM_ID });

  const onSubmit = async (values: SpeechStrategyForm) => {
    const persisted = await save(fromForm(values));
    form.reset(toForm(forRow(persisted), language), CLEAR_DIRTY_VALUES);
  };

  const loadStatuses = [getStatus, listStatus, providersStatus, sttStatus, ttsStatus];
  let loadStatus: 'pending' | 'error' | 'success' = 'pending';
  if (loadStatuses.includes('error')) {
    loadStatus = 'error';
  } else if (loadStatuses.every((status) => status === 'success')) {
    loadStatus = 'success';
  }

  const reload = () =>
    Promise.all([refetch(), refetchList(), refetchProviders(), refetchStt(), refetchTts()]);

  return {
    form,
    onSubmit,
    loadStatus,
    reload,
    canRead,
    canWrite,
    isDirty,
    isMutating: saveStatus === 'pending',
  };
};
